import { useAuth0 } from '@auth0/auth0-react';
import { useMe } from '@/api/hooks';

/**
 * Core auth hook — wraps Auth0 SDK.
 * Use this for auth actions (login, logout, token) and raw auth state.
 */
export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const login = (returnTo) =>
    loginWithRedirect({
      authorizationParams: { ui_locales: 'he' },
      appState: { returnTo: returnTo || window.location.pathname },
    });

  const signup = (returnTo) =>
    loginWithRedirect({
      authorizationParams: { screen_hint: 'signup', ui_locales: 'he' },
      appState: { returnTo: returnTo || window.location.pathname },
    });

  const logout = () =>
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });

  const getToken = () => getAccessTokenSilently();

  return {
    user,
    isAuthenticated,
    isLoadingAuth: isLoading,
    login,
    signup,
    logout,
    getToken,
  };
}

/**
 * User profile hook — combines Auth0 identity with DB profile.
 * Returns role, capabilities, points, referral code, and all DB-stored user data.
 * Use this when you need to know WHO the user is (not just IF they're logged in).
 *
 * `capabilities` mirrors the server's RoleHelper truth — never derive admin
 * gating from `role === 'admin'` directly; check capabilities instead so the
 * UI stays correct as roles evolve.
 */
export function useUserProfile() {
  const { isAuthenticated, user: auth0User } = useAuth();
  const { data: profile, isLoading } = useMe();

  const capabilities = profile?.capabilities ?? {};

  return {
    profile: profile || null,
    role: profile?.role ?? null,
    capabilities,
    isAdmin: Boolean(capabilities.is_admin),
    canReview: Boolean(capabilities.review),
    canEditContent: Boolean(capabilities.edit_content),
    canModerateUsers: Boolean(capabilities.moderate_users),
    canChangeRoles: Boolean(capabilities.change_roles),
    canViewAuditLog: Boolean(capabilities.view_audit_log),
    isLoading: isLoading && isAuthenticated,
    isAuthenticated,
    displayName: profile?.full_name || auth0User?.name || auth0User?.email?.split('@')[0] || '',
    email: profile?.email || auth0User?.email || '',
  };
}
